import clsx from 'clsx'
import { ReactNode } from 'react'
import Header from '../Header/Header'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'

export interface BookingSectionProps {
  /**
   * Section title
   */
  title: string
  /**
   * Completed step state
   */
  completed: boolean
  /**
   * Show content independent of completed state
   */
  showContent?: boolean
  /**
   * Sequential section step number
   */
  step?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
  /**
   * Section is disabled or not reached yet
   */
  disabled?: boolean
  /**
   * Section is currently expanded/active
   */
  isExpanded?: boolean
  /**
   * Redirect link redirect to a different edit route or view
   */
  edit?: string | boolean
  /**
   * Additional classes
   */
  className?: string
  /**
   * Section content
   */
  children: ReactNode
  /**
   * Edit action callback
   */
  onEdit?: () => void
}

export default function BookingSection({
  title,
  completed,
  showContent,
  step,
  disabled,
  isExpanded,
  edit,
  className,
  children,
  onEdit,
}: BookingSectionProps) {
  const handleEdit = () => {
    if (disabled) return

    onEdit?.()
  }

  const enabledHeading = !disabled && completed
  const isHiddenChildren = disabled || (completed && !showContent)
  const shouldGreyOut = !disabled && !isExpanded

  return (
    <div
      className={clsx('bg-neutral-200 px-4 py-6 lg:p-8 rounded-lg', className)}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 lg:gap-3">
          {enabledHeading ? (
            <Icon
              icon="check-circle-filled"
              className="text-theme-accessibility w-5 lg:w-8 h-5 lg:h-8"
            />
          ) : (
            step && (
              <Icon
                icon={`number-${step}-circle`}
                className={clsx(
                  'w-5 lg:w-8 h-5 lg:h-8',
                  !shouldGreyOut ? 'text-neutral-600' : 'text-neutral-700',
                )}
              />
            )
          )}
          <Header
            as="h2"
            className={clsx(
              'type-body-2 lg:type-body-1 w-full',
              !shouldGreyOut ? 'text-neutral-600' : 'text-neutral-700',
            )}
          >
            {title}
          </Header>
        </div>
        {enabledHeading && (
          <div className="[&_a]:inline">
            <Link
              href={typeof edit === 'string' ? edit : '#'}
              className={clsx(
                disabled || shouldGreyOut
                  ? 'text-neutral-600 visited:text-neutral-600'
                  : 'text-neutral-700 visited:text-neutral-700',
              )}
              onClick={handleEdit}
            >
              Edit
            </Link>
          </div>
        )}
      </div>
      {children && (
        <div
          className={clsx(step !== 4 && 'pt-4 md:pt-6 lg:pt-8', {
            hidden: isHiddenChildren,
          })}
        >
          {children}
        </div>
      )}
    </div>
  )
}
